import { useState } from 'react';
import { Ban, Loader2 } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

interface BlockDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (reason: string) => void;
  isLoading: boolean;
  /** Name of the location being blocked (for single block) */
  locationName?: string;
  /** Number of locations being blocked (for batch block) */
  locationCount?: number;
}

/**
 * Dialog for blocking one or more synced locations.
 * Requires a reason to be provided (min 10 characters).
 */
export function BlockDialog({
  open,
  onOpenChange,
  onConfirm,
  isLoading,
  locationName,
  locationCount,
}: BlockDialogProps): JSX.Element {
  const [reason, setReason] = useState('');
  const isBatch = locationCount !== undefined && locationCount > 1;
  const isValid = reason.trim().length >= 10;

  const handleConfirm = (): void => {
    if (isValid) {
      onConfirm(reason.trim());
    }
  };

  const handleOpenChange = (newOpen: boolean): void => {
    if (!newOpen) {
      // Reset state when closing
      setReason('');
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent data-testid="block-dialog">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <Ban className="h-5 w-5 text-destructive" />
            <DialogTitle>
              {isBatch
                ? `Block ${String(locationCount)} Locations`
                : 'Block Location'}
            </DialogTitle>
          </div>
          <DialogDescription>
            {isBatch
              ? `You are about to block ${String(locationCount)} locations. Blocked locations cannot be published to ScoutAround.`
              : `Block "${locationName ?? 'this location'}" from being published to ScoutAround.`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="block-reason">
              Reason for blocking <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="block-reason"
              placeholder="Enter the reason for blocking this location (minimum 10 characters)"
              value={reason}
              onChange={(e) => { setReason(e.target.value); }}
              className="min-h-[100px]"
              data-testid="block-reason-input"
            />
            <p className="text-xs text-muted-foreground">
              {reason.trim().length}/10 characters minimum
            </p>
          </div>

          <div className="rounded-lg border bg-muted/30 p-4">
            <p className="text-sm font-medium mb-2">Blocking a location will:</p>
            <ul className="list-inside list-disc space-y-1 text-sm text-muted-foreground">
              <li>Prevent it from being published to ScoutAround</li>
              <li>Exclude it from batch publish operations</li>
              <li>Display a &quot;Blocked&quot; badge in the locations list</li>
            </ul>
            <p className="text-sm mt-3">
              You can unblock the location at any time.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => { handleOpenChange(false); }}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={isLoading || !isValid}
            data-testid="block-confirm-btn"
          >
            {isLoading ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : null}
            Block
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
